import React, { useState, useEffect, useCallback } from 'react';
import { 
  Layout,
  Card,
  Button, 
  InlineStack, 
  BlockStack,
  Badge,
  Modal,
  Form,
  FormLayout,
  TextField,
  Banner,
  Spinner,
  Text,
  Checkbox,
  DataTable,
  Select,
  Collapsible
} from '@shopify/polaris';
import { ChevronDownIcon, ChevronRightIcon, ChevronUpIcon, PlusIcon } from '@shopify/polaris-icons';
import { addOnsAPI } from '../services/api';
import shopifyApi from '../services/shopifyApi';

const AddOns = () => {
  const [addOns, setAddOns] = useState([]);
  const [shopifyProducts, setShopifyProducts] = useState([]);
  const [filteredAddOns, setFilteredAddOns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalActive, setModalActive] = useState(false);
  const [editingAddOn, setEditingAddOn] = useState(null);
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Entry management state
  const [entryModalActive, setEntryModalActive] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [selectedAddOnForEntry, setSelectedAddOnForEntry] = useState(null);
  const [expandedAddOns, setExpandedAddOns] = useState(new Set());
  
  // Form state for add-on
  const [addOnFormData, setAddOnFormData] = useState({
    name: '',
    description: '',
    category: '',
    allowMultipleSelections: false,
    maxSelections: 1,
    tags: '',
    sortOrder: 0
  });
  
  // Form state for entry
  const [entryFormData, setEntryFormData] = useState({
    shopifyProductId: '',
    shopifyVariantId: '',
    title: '',
    images: [],
    availableQuantity: 1,
    pricingType: 'included',
    price: '',
    displayName: '',
    description: '',
    sortOrder: 0
  });

  // Entry creation state
  const [selectedShopifyProducts, setSelectedShopifyProducts] = useState([]);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [loadingShopify, setLoadingShopify] = useState(false);

  const fetchAddOns = useCallback(async () => {
    try {
      setLoading(true);
      const response = await addOnsAPI.getAll({ includeEntries: true, limit: 1000 });
      setAddOns(response.data?.addOns || []);
    } catch (err) {
      setError('Failed to load add-ons');
      setAddOns([]);
      console.error('Add-ons error:', err);
    } finally {
      setLoading(false);
    }
  }, []);


  const fetchShopifyProducts = useCallback(async () => {
    try {
      setLoadingShopify(true);
      const response = await shopifyApi.getProducts();
      setShopifyProducts(response.products || []);
    } catch (err) {
      console.error('Failed to fetch Shopify products:', err);
      setShopifyProducts([]);
    } finally {
      setLoadingShopify(false);
    }
  }, []);

  useEffect(() => {
    fetchAddOns();
    fetchShopifyProducts();
  }, [fetchAddOns, fetchShopifyProducts]);

  // Filter and search add-ons
  useEffect(() => {
    let filtered = [...(addOns || [])];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(addOn =>
        addOn.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        addOn.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        addOn.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (addOn.tags && Array.isArray(addOn.tags) && addOn.tags.some(tag => 
          tag?.toLowerCase().includes(searchQuery.toLowerCase())
        ))
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'active') {
        filtered = filtered.filter(addOn => addOn.isActive);
      } else if (statusFilter === 'inactive') {
        filtered = filtered.filter(addOn => !addOn.isActive);
      }
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(addOn => addOn.category === categoryFilter);
    }

    // Sort
    filtered.sort((a, b) => {
      if (a.sortOrder !== b.sortOrder) {
        return a.sortOrder - b.sortOrder;
      }
      return a.name.localeCompare(b.name);
    });

    setFilteredAddOns(filtered);
  }, [addOns, searchQuery, statusFilter, categoryFilter]);

  const getUniqueCategories = () => {
    const categories = addOns
      .map(addOn => addOn.category)
      .filter(cat => cat && cat.trim() !== '')
      .filter((cat, index, array) => array.indexOf(cat) === index)
      .sort();
    return categories;
  };

  const handleAddOnFormChange = (field, value) => {
    setAddOnFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEntryFormChange = (field, value) => {
    setEntryFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetAddOnForm = () => {
    setAddOnFormData({
      name: '',
      description: '',
      category: '',
      allowMultipleSelections: false,
      maxSelections: 1,
      tags: '',
      sortOrder: 0
    });
    setEditingAddOn(null);
  };

  const resetEntryForm = () => {
    setEntryFormData({
      shopifyProductId: '',
      shopifyVariantId: '',
      title: '',
      images: [],
      availableQuantity: 1,
      pricingType: 'included',
      price: '',
      displayName: '',
      description: '',
      sortOrder: 0
    });
    setSelectedShopifyProducts([]);
    setSelectedVariants({});
    setEditingEntry(null);
    setSelectedAddOnForEntry(null);
  };

  const handleAddOnModalClose = () => {
    setModalActive(false);
    resetAddOnForm();
  };

  const handleEntryModalClose = () => {
    setEntryModalActive(false);
    resetEntryForm();
  };

  const handleEditAddOn = (addOn) => {
    setEditingAddOn(addOn);
    setAddOnFormData({
      name: addOn.name,
      description: addOn.description || '',
      category: addOn.category || '',
      allowMultipleSelections: addOn.allowMultipleSelections || false,
      maxSelections: addOn.maxSelections || 1,
      tags: addOn.tags ? addOn.tags.join(', ') : '',
      sortOrder: addOn.sortOrder || 0
    });
    setModalActive(true);
  };

  const handleAddEntry = (addOn) => {
    setSelectedAddOnForEntry(addOn);
    setEntryModalActive(true);
  };

  const handleEditEntry = (addOn, entry) => {
    setSelectedAddOnForEntry(addOn);
    setEditingEntry(entry);
    setEntryFormData({
      shopifyProductId: entry.shopifyProductId || '',
      shopifyVariantId: entry.shopifyVariantId || '',
      title: entry.title || '',
      images: entry.images || [],
      availableQuantity: entry.availableQuantity || 1,
      pricingType: entry.pricingType,
      price: entry.price || '',
      displayName: entry.displayName || '',
      description: entry.description || '',
      sortOrder: entry.sortOrder || 0
    });
    
    // Set up Shopify product selection for editing
    if (entry.shopifyProductId) {
      setSelectedShopifyProducts([entry.shopifyProductId]);
      if (entry.shopifyVariantId) {
        setSelectedVariants({ [entry.shopifyProductId]: entry.shopifyVariantId });
      }
    }
    
    setEntryModalActive(true);
  };

  const handleAddOnSubmit = async () => {
    try {
      const submitData = {
        ...addOnFormData,
        tags: addOnFormData.tags ? addOnFormData.tags.split(',').map(t => t.trim()).filter(t => t) : []
      };

      if (editingAddOn) {
        await addOnsAPI.update(editingAddOn.id, submitData);
      } else {
        await addOnsAPI.create(submitData);
      }

      handleAddOnModalClose();
      fetchAddOns();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save add-on');
    }
  };

  const handleEntrySubmit = async () => {
    try {
      if (editingEntry) {
        // Update existing entry
        const submitData = { ...entryFormData };
        
        // Convert price to number if needed
        if (submitData.pricingType !== 'included' && submitData.price) {
          submitData.price = parseFloat(submitData.price);
        } else if (submitData.pricingType === 'included') {
          submitData.price = null;
        }

        await addOnsAPI.updateEntry(selectedAddOnForEntry.id, editingEntry.id, submitData);
      } else {
        // Create new entries from selected Shopify products
        const entriesToCreate = [];
        
        for (const productId of selectedShopifyProducts) {
          const shopifyProduct = shopifyProducts.find(p => p.id.toString() === productId);
          if (!shopifyProduct) continue;
          
          // If variants exist and one is selected, create entry for that variant
          const variantId = selectedVariants[productId];
          if (shopifyProduct.variants && shopifyProduct.variants.length > 0) {
            if (variantId) {
              const variant = shopifyProduct.variants.find(v => v.id.toString() === variantId);
              if (variant) {
                const entryData = {
                  ...entryFormData,
                  shopifyProductId: productId,
                  shopifyVariantId: variantId,
                  title: `${shopifyProduct.title} - ${variant.title}`,
                  images: variant.image ? [variant.image] : shopifyProduct.images || [],
                  availableQuantity: entryFormData.availableQuantity || variant.inventoryQuantity || 1
                };
                
                // Convert price to number if needed
                if (entryData.pricingType !== 'included' && entryData.price) {
                  entryData.price = parseFloat(entryData.price);
                } else if (entryData.pricingType === 'included') {
                  entryData.price = null;
                }
                
                entriesToCreate.push(entryData);
              }
            } else {
              // Create entries for all variants if none selected
              for (const variant of shopifyProduct.variants) {
                const entryData = {
                  ...entryFormData,
                  shopifyProductId: productId,
                  shopifyVariantId: variant.id.toString(),
                  title: `${shopifyProduct.title} - ${variant.title}`,
                  images: variant.image ? [variant.image] : shopifyProduct.images || [],
                  availableQuantity: entryFormData.availableQuantity || variant.inventoryQuantity || 1
                };
                
                // Convert price to number if needed
                if (entryData.pricingType !== 'included' && entryData.price) {
                  entryData.price = parseFloat(entryData.price);
                } else if (entryData.pricingType === 'included') {
                  entryData.price = null;
                }
                
                entriesToCreate.push(entryData);
              }
            }
          } else {
            // Product without variants
            const entryData = {
              ...entryFormData,
              shopifyProductId: productId,
              shopifyVariantId: null,
              title: shopifyProduct.title,
              images: shopifyProduct.images || [],
              availableQuantity: entryFormData.availableQuantity || 1
            };
            
            // Convert price to number if needed
            if (entryData.pricingType !== 'included' && entryData.price) {
              entryData.price = parseFloat(entryData.price);
            } else if (entryData.pricingType === 'included') {
              entryData.price = null;
            }
            
            entriesToCreate.push(entryData);
          }
        }
        
        // Create all entries
        for (const entryData of entriesToCreate) {
          await addOnsAPI.createEntry(selectedAddOnForEntry.id, entryData);
        }
      }

      handleEntryModalClose();
      fetchAddOns();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save add-on entry');
    }
  };

  const handleToggleActive = async (addOnId, isActive) => {
    if (window.confirm(`Are you sure you want to ${isActive ? 'deactivate' : 'activate'} this add-on?`)) {
      try {
        await addOnsAPI.toggleActive(addOnId);
        fetchAddOns();
      } catch (err) {
        setError('Failed to update add-on status');
      }
    }
  };

  const handleDeleteAddOn = async (addOnId) => {
    if (window.confirm('Are you sure you want to delete this add-on? This will also delete all entries and cannot be undone.')) {
      try {
        await addOnsAPI.delete(addOnId);
        fetchAddOns();
      } catch (err) {
        setError('Failed to delete add-on');
      }
    }
  };

  const handleDeleteEntry = async (addOnId, entryId) => {
    if (window.confirm('Are you sure you want to remove this entry from the add-on?')) {
      try {
        await addOnsAPI.deleteEntry(addOnId, entryId);
        fetchAddOns();
      } catch (err) {
        setError('Failed to delete add-on entry');
      }
    }
  };

  const handleMoveEntryUp = async (addOn, entry, currentIndex) => {
    if (currentIndex === 0) return; // Already at top
    
    try {
      const entries = addOn.entries || [];
      console.log('🔼 Moving entry UP:', {
        addOnId: addOn.id,
        entryId: entry.id,
        currentIndex,
        entriesBefore: entries.map((e, i) => ({ index: i, id: e.id, title: e.displayName || e.title, sortOrder: e.sortOrder }))
      });
      
      // Create a new array with swapped positions
      const reorderedEntries = [...entries];
      [reorderedEntries[currentIndex - 1], reorderedEntries[currentIndex]] = 
        [reorderedEntries[currentIndex], reorderedEntries[currentIndex - 1]];
      
      // Assign sequential sortOrder values based on new positions
      const updates = reorderedEntries.map((e, index) => ({
        entryId: e.id,
        sortOrder: index
      }));
      
      console.log('🔼 Sending updates:', updates);
      console.log('🔼 Expected order:', reorderedEntries.map((e, i) => ({ index: i, id: e.id, title: e.displayName || e.title })));
      
      await addOnsAPI.reorderEntries(addOn.id, updates);
      fetchAddOns();
    } catch (err) {
      setError('Failed to reorder entries');
      console.error('❌ Move entry up error:', err);
    }
  };

  const handleMoveEntryDown = async (addOn, entry, currentIndex) => {
    const entries = addOn.entries || [];
    if (currentIndex === entries.length - 1) return; // Already at bottom
    
    try {
      console.log('🔽 Moving entry DOWN:', {
        addOnId: addOn.id,
        entryId: entry.id,
        currentIndex,
        entriesBefore: entries.map((e, i) => ({ index: i, id: e.id, title: e.displayName || e.title, sortOrder: e.sortOrder }))
      });
      
      // Create a new array with swapped positions
      const reorderedEntries = [...entries];
      [reorderedEntries[currentIndex], reorderedEntries[currentIndex + 1]] = 
        [reorderedEntries[currentIndex + 1], reorderedEntries[currentIndex]];
      
      // Assign sequential sortOrder values based on new positions
      const updates = reorderedEntries.map((e, index) => ({
        entryId: e.id,
        sortOrder: index
      }));
      
      console.log('🔽 Sending updates:', updates);
      console.log('🔽 Expected order:', reorderedEntries.map((e, i) => ({ index: i, id: e.id, title: e.displayName || e.title })));
      
      await addOnsAPI.reorderEntries(addOn.id, updates);
      fetchAddOns();
    } catch (err) {
      setError('Failed to reorder entries');
      console.error('❌ Move entry down error:', err);
    }
  };

  const toggleAddOnExpanded = (addOnId) => {
    setExpandedAddOns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(addOnId)) {
        newSet.delete(addOnId);
      } else {
        newSet.add(addOnId);
      }
      return newSet;
    });
  };

  // Shopify product selection handlers
  const handleShopifyProductSelect = (productId, isSelected) => {
    if (isSelected) {
      setSelectedShopifyProducts(prev => [...prev, productId]);
    } else {
      setSelectedShopifyProducts(prev => prev.filter(id => id !== productId));
      // Remove variant selection for this product
      setSelectedVariants(prev => {
        const newVariants = { ...prev };
        delete newVariants[productId];
        return newVariants;
      });
    }
  };

  const handleVariantSelect = (productId, variantId) => {
    setSelectedVariants(prev => ({
      ...prev,
      [productId]: variantId
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getStatusBadge = (isActive) => {
    return isActive ? 
      <Badge status="success">Active</Badge> : 
      <Badge status="warning">Inactive</Badge>;
  };

  const getPricingBadge = (entry) => {
    switch (entry.pricingType) {
      case 'included':
        return <Badge>Included</Badge>;
      case 'fixed_price':
        return <Badge status="info">Fixed Price</Badge>;
      case 'percentage_of_base':
        return <Badge status="attention">% of Base</Badge>;
      default:
        return <Badge>Unknown</Badge>;
    }
  };

  const getPriceDisplay = (entry) => {
    switch (entry.pricingType) {
      case 'included':
        return 'Included';
      case 'fixed_price':
        return formatCurrency(entry.price);
      case 'percentage_of_base':
        return `${entry.price}% of base`;
      default:
        return 'Unknown';
    }
  };

  if (loading && (!addOns || addOns.length === 0)) {
    return (
      <Layout>
        <Layout.Section>
          <Card sectioned>
            <InlineStack align="center" gap="400">
              <Spinner size="large" />
              <Text variant="bodyMd">Loading add-ons...</Text>
            </InlineStack>
          </Card>
        </Layout.Section>
      </Layout>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <Text variant="headingLg" as="h1">Add-Ons</Text>
      </div>
      {error && (
        <Layout>
          <Layout.Section>
            <Banner status="critical" onDismiss={() => setError('')}>
              {error}
            </Banner>
          </Layout.Section>
        </Layout>
      )}

      <Layout>
        <Layout.Section>
          {/* Search and Filter Controls */}
          <Card sectioned>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text variant="headingMd">Add-Ons ({(filteredAddOns || []).length})</Text>
                <InlineStack gap="200">
                  <Button onClick={() => setModalActive(true)}>Add Add-On</Button>
                </InlineStack>
              </InlineStack>
              
              {(addOns && addOns.length > 0) && (
                <InlineStack gap="400" wrap={false}>
                  <div style={{ minWidth: '300px' }}>
                    <TextField
                      label="Search add-ons"
                      value={searchQuery}
                      onChange={setSearchQuery}
                      placeholder="Search by name, category, or tags..."
                      clearButton
                      onClearButtonClick={() => setSearchQuery('')}
                    />
                  </div>
                  
                  <div style={{ minWidth: '150px' }}>
                    <Select
                      label="Status"
                      options={[
                        { label: 'All', value: 'all' },
                        { label: 'Active', value: 'active' },
                        { label: 'Inactive', value: 'inactive' },
                      ]}
                      value={statusFilter}
                      onChange={setStatusFilter}
                    />
                  </div>
                  
                  <div style={{ minWidth: '150px' }}>
                    <Select
                      label="Category"
                      options={[
                        { label: 'All Categories', value: 'all' },
                        ...getUniqueCategories().map(cat => ({ label: cat, value: cat }))
                      ]}
                      value={categoryFilter}
                      onChange={setCategoryFilter}
                    />
                  </div>
                </InlineStack>
              )}
            </BlockStack>
          </Card>

          {(addOns && addOns.length > 0) ? (
            <Card>
              <BlockStack gap="0">
                {filteredAddOns.map((addOn) => {
                  const isExpanded = expandedAddOns.has(addOn.id);
                  // Sort entries by sortOrder (ascending: 0, 1, 2, 3...)
                  const entries = (addOn.entries || []).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
                  
                  return (
                    <div key={addOn.id} style={{ borderBottom: '1px solid #e1e3e5' }}>
                      {/* Add-On Header */}
                      <div style={{ padding: '16px' }}>
                        <InlineStack align="space-between" gap="400">
                          <InlineStack gap="300" align="center">
                            <Button
                              variant="plain"
                              onClick={() => toggleAddOnExpanded(addOn.id)}
                              icon={isExpanded ? ChevronDownIcon : ChevronRightIcon}
                            />
                            
                            <BlockStack gap="100">
                              <InlineStack gap="200" align="center">
                                <Text variant="headingSm" fontWeight="semibold">
                                  {addOn.name}
                                </Text>
                                {getStatusBadge(addOn.isActive)}
                                {addOn.allowMultipleSelections && (
                                  <Badge size="small" status="info">
                                    Multi-Select (max {addOn.maxSelections})
                                  </Badge>
                                )}
                              </InlineStack>
                              
                              {addOn.description && (
                                <Text variant="bodySm" tone="subdued">
                                  {addOn.description}
                                </Text>
                              )}
                              
                              <InlineStack gap="200">
                                {addOn.category && (
                                  <Text variant="bodySm" tone="subdued">
                                    Category: {addOn.category}
                                  </Text>
                                )}
                                <Text variant="bodySm" tone="subdued">
                                  {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
                                </Text>
                              </InlineStack>
                            </BlockStack>
                          </InlineStack>

                          <InlineStack gap="200">
                            <Button
                              size="slim"
                              variant="secondary"
                              onClick={() => handleAddEntry(addOn)}
                              icon={PlusIcon}
                            >
                              Add Entry
                            </Button>
                            <Button 
                              size="slim"
                              variant="secondary"
                              onClick={() => handleEditAddOn(addOn)}
                            >
                              Edit
                            </Button>
                            <Button 
                              size="slim"
                              variant="secondary"
                              tone={addOn.isActive ? "critical" : "success"}
                              onClick={() => handleToggleActive(addOn.id, addOn.isActive)}
                            >
                              {addOn.isActive ? 'Deactivate' : 'Activate'}
                            </Button>
                            <Button 
                              size="slim"
                              variant="secondary"
                              tone="critical"
                              onClick={() => handleDeleteAddOn(addOn.id)}
                            >
                              Delete
                            </Button>
                          </InlineStack>
                        </InlineStack>
                      </div>

                      {/* Entries List */}
                      <Collapsible open={isExpanded} id={`addon-${addOn.id}-entries`}>
                        <div style={{ padding: '0 16px 16px 64px' }}>
                          {entries.length === 0 ? (
                            <Card sectioned>
                              <Text variant="bodyMd" tone="subdued" alignment="center">
                                No entries added yet. Click "Add Entry" to get started.
                              </Text>
                            </Card>
                          ) : (
                            <Card>
                              <DataTable
                                columnContentTypes={['text', 'text', 'text', 'text', 'text']}
                                headings={['Product', 'Pricing', 'Price', 'Availability', 'Actions']}
                                rows={entries.map((entry, entryIndex) => [
                                  // Product
                                  <InlineStack key={`product-${entry.id}`} gap="200" align="start">
                                    {entry.images && entry.images.length > 0 && (
                                      <img
                                        src={entry.images[0]}
                                        alt={entry.title}
                                        style={{
                                          width: '50px',
                                          height: '50px',
                                          objectFit: 'cover',
                                          borderRadius: '4px',
                                          border: '1px solid #e1e3e5'
                                        }}
                                      />
                                    )}
                                    <BlockStack gap="100">
                                      <Text variant="bodyMd" fontWeight="semibold">
                                        {entry.displayName || entry.title || 'Unknown Product'}
                                      </Text>
                                      {entry.description && (
                                        <Text variant="bodySm" tone="subdued">
                                          {entry.description}
                                        </Text>
                                      )}
                                      <InlineStack gap="100">
                                        {entryIndex === 0 && (
                                          <Badge size="small" status="success">Default</Badge>
                                        )}
                                        <Badge size="small" tone="info">Shopify Product</Badge>
                                        {entry.shopifyVariantId && (
                                          <Badge size="small" tone="subdued">Variant</Badge>
                                        )}
                                      </InlineStack>
                                      <Text variant="bodySm" tone="subdued">
                                        Product ID: {entry.shopifyProductId}
                                        {entry.shopifyVariantId && ` • Variant: ${entry.shopifyVariantId}`}
                                      </Text>
                                    </BlockStack>
                                  </InlineStack>,
                                  
                                  // Pricing Type
                                  getPricingBadge(entry),
                                  
                                  // Price
                                  <Text key={`price-${entry.id}`} variant="bodyMd">
                                    {getPriceDisplay(entry)}
                                  </Text>,
                                  
                                  // Availability
                                  <InlineStack key={`availability-${entry.id}`} gap="200">
                                    {entry.isActive ? (
                                      <Badge status="success">Active</Badge>
                                    ) : (
                                      <Badge status="critical">Inactive</Badge>
                                    )}
                                    <Text variant="bodySm" tone="subdued">
                                      {entry.availableQuantity || 0} available
                                    </Text>
                                  </InlineStack>,
                                  
                                  // Actions
                                  <InlineStack key={`actions-${entry.id}`} gap="100">
                                    <Button 
                                      size="slim"
                                      variant="secondary"
                                      icon={ChevronUpIcon}
                                      onClick={() => handleMoveEntryUp(addOn, entry, entryIndex)}
                                      disabled={entryIndex === 0}
                                      accessibilityLabel="Move up"
                                    />
                                    <Button 
                                      size="slim"
                                      variant="secondary"
                                      icon={ChevronDownIcon}
                                      onClick={() => handleMoveEntryDown(addOn, entry, entryIndex)}
                                      disabled={entryIndex === entries.length - 1}
                                      accessibilityLabel="Move down"
                                    />
                                    <Button 
                                      size="slim"
                                      variant="secondary"
                                      onClick={() => handleEditEntry(addOn, entry)}
                                    >
                                      Edit
                                    </Button>
                                    <Button 
                                      size="slim"
                                      variant="secondary"
                                      tone="critical"
                                      onClick={() => handleDeleteEntry(addOn.id, entry.id)}
                                    >
                                      Remove
                                    </Button>
                                  </InlineStack>
                                ])}
                              />
                            </Card>
                          )}
                        </div>
                      </Collapsible>
                    </div>
                  );
                })}
              </BlockStack>
            </Card>
          ) : (
            <Card>
              <div style={{ padding: '16px', textAlign: 'center' }}>
                <Text variant="bodyMd" tone="subdued">
                  No add-ons found. Create your first add-on to get started.
                </Text>
              </div>
            </Card>
          )}
        </Layout.Section>
      </Layout>

      {/* Add-On Modal */}
      <Modal
        open={modalActive}
        onClose={handleAddOnModalClose}
        title={editingAddOn ? 'Edit Add-On' : 'Create Add-On'}
        primaryAction={{
          content: editingAddOn ? 'Update' : 'Create',
          onAction: handleAddOnSubmit,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: handleAddOnModalClose,
          },
        ]}
      >
        <Modal.Section>
          <Form onSubmit={handleAddOnSubmit}>
            <FormLayout>
              <TextField
                label="Name"
                value={addOnFormData.name}
                onChange={(value) => handleAddOnFormChange('name', value)}
                required
                helpText="e.g., 'Goggles Male', 'Gloves Selection', 'Protection Kit'"
              />

              <TextField
                label="Description"
                value={addOnFormData.description}
                onChange={(value) => handleAddOnFormChange('description', value)}
                helpText="Optional description for this add-on"
                multiline={3}
              />

              <FormLayout.Group>
                <TextField
                  label="Category"
                  value={addOnFormData.category}
                  onChange={(value) => handleAddOnFormChange('category', value)}
                  helpText="e.g., 'Accessories', 'Protection', 'Upgrades'"
                />

                <TextField
                  label="Sort Order"
                  type="number"
                  value={addOnFormData.sortOrder}
                  onChange={(value) => handleAddOnFormChange('sortOrder', value)}
                  helpText="Lower numbers appear first"
                />
              </FormLayout.Group>

              <TextField
                label="Tags (comma-separated)"
                value={addOnFormData.tags}
                onChange={(value) => handleAddOnFormChange('tags', value)}
                helpText="e.g., 'winter, protective, premium'"
              />

              <Checkbox
                label="Allow multiple selections"
                checked={addOnFormData.allowMultipleSelections}
                onChange={(checked) => handleAddOnFormChange('allowMultipleSelections', checked)}
                helpText="If enabled, customers can select multiple entries from this add-on"
              />

              {addOnFormData.allowMultipleSelections && (
                <TextField
                  label="Maximum selections"
                  type="number"
                  min="1"
                  value={addOnFormData.maxSelections}
                  onChange={(value) => handleAddOnFormChange('maxSelections', value)}
                  helpText="Maximum number of entries customer can select"
                />
              )}
            </FormLayout>
          </Form>
        </Modal.Section>
      </Modal>

      {/* Entry Modal */}
      <Modal
        open={entryModalActive}
        onClose={handleEntryModalClose}
        title={editingEntry ? 'Edit Add-On Entry' : `Add Entry to "${selectedAddOnForEntry?.name}"`}
        primaryAction={{
          content: editingEntry ? 'Update' : 'Add Entry',
          onAction: handleEntrySubmit,
          disabled: editingEntry ? false : selectedShopifyProducts.length === 0
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: handleEntryModalClose,
          },
        ]}
      >
        <Modal.Section>
          <Form onSubmit={handleEntrySubmit}>
            <FormLayout>
              {/* Shopify Product Selection */}
              <div>
                <Text variant="bodyMd" fontWeight="semibold" as="label">
                  {editingEntry ? 'Shopify Product' : 'Select Shopify Products'}
                </Text>
                <Text variant="bodySm" tone="subdued" as="p">
                  {editingEntry 
                    ? 'This entry is linked to the Shopify product below.'
                    : 'Choose products to add as entries. Each variant will be created as a separate entry with the same pricing configuration below.'
                  }
                </Text>
                {loadingShopify ? (
                  <div style={{ padding: '16px', textAlign: 'center' }}>
                    <Spinner size="small" />
                    <Text variant="bodyMd" tone="subdued">Loading products...</Text>
                  </div>
                ) : (
                  <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e1e3e5', borderRadius: '4px', padding: '8px' }}>
                    {shopifyProducts.length === 0 ? (
                      <div style={{ padding: '16px', textAlign: 'center' }}>
                        <Text variant="bodyMd" tone="subdued">No products available</Text>
                      </div>
                    ) : (
                      (editingEntry && selectedShopifyProducts.length > 0 
                        ? shopifyProducts.filter(product => selectedShopifyProducts.includes(product.id.toString()))
                        : shopifyProducts
                      ).map(product => (
                        <div key={product.id} style={{ marginBottom: '8px' }}>
                          <div
                            style={{
                              padding: '12px',
                              border: selectedShopifyProducts.includes(product.id.toString()) ? '2px solid #008060' : '1px solid #e1e3e5',
                              borderRadius: '4px',
                              backgroundColor: selectedShopifyProducts.includes(product.id.toString()) ? '#f0f9f7' : 'white',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <InlineStack gap="300" align="start">
                              <Checkbox
                                checked={selectedShopifyProducts.includes(product.id.toString())}
                                onChange={(checked) => handleShopifyProductSelect(product.id.toString(), checked)}
                                disabled={editingEntry}
                              />
                              {product.images && product.images.length > 0 && (
                                <img
                                  src={product.images[0]}
                                  alt={product.title}
                                  style={{
                                    width: '60px',
                                    height: '60px',
                                    objectFit: 'cover',
                                    borderRadius: '4px',
                                    border: '1px solid #e1e3e5'
                                  }}
                                />
                              )}
                              <BlockStack gap="100">
                                <Text variant="bodyMd" fontWeight="semibold">
                                  {product.title}
                                </Text>
                                <Text variant="bodySm" tone="subdued">
                                  {product.productType} • {product.status}
                                </Text>
                                {product.tags && product.tags.length > 0 && (
                                  <InlineStack gap="100">
                                    {product.tags.slice(0, 3).map(tag => (
                                      <Badge key={tag} size="small">{tag}</Badge>
                                    ))}
                                  </InlineStack>
                                )}
                              </BlockStack>
                            </InlineStack>
                          </div>
                          
                          {/* Show variants if this product is selected and has variants */}
                          {((editingEntry && selectedShopifyProducts.includes(product.id.toString())) || 
                            (!editingEntry && selectedShopifyProducts.includes(product.id.toString()))) && 
                            product.hasVariants && (
                            <div style={{ marginLeft: '20px', marginTop: '8px' }}>
                              <Text variant="bodySm" fontWeight="semibold" tone="subdued">
                                {editingEntry ? 'Selected Variant:' : 'Select Variant (optional - leave blank to create all):'}
                              </Text>
                              {product.variants.map(variant => {
                                const isSelected = selectedVariants[product.id.toString()] === variant.id.toString();
                                
                                return (
                                  <div
                                    key={variant.id}
                                    style={{
                                      padding: '8px 12px',
                                      marginTop: '4px',
                                      border: isSelected ? '2px solid #008060' : '1px solid #e1e3e5',
                                      borderRadius: '4px',
                                      cursor: editingEntry ? 'default' : 'pointer',
                                      backgroundColor: isSelected ? '#f0f9f7' : '#f9f9f9',
                                      transition: 'all 0.2s ease'
                                    }}
                                    onClick={editingEntry ? undefined : () => handleVariantSelect(product.id.toString(), variant.id.toString())}
                                  >
                                    <InlineStack gap="200" align="center">
                                      {variant.image && (
                                        <img
                                          src={variant.image}
                                          alt={variant.title}
                                          style={{
                                            width: '40px',
                                            height: '40px',
                                            objectFit: 'cover',
                                            borderRadius: '4px',
                                            border: '1px solid #e1e3e5'
                                          }}
                                        />
                                      )}
                                      <BlockStack gap="50">
                                        <Text variant="bodySm" fontWeight="medium">
                                          {variant.title}
                                        </Text>
                                        <Text variant="bodySm" tone="subdued">
                                          ${variant.price} • Qty: {variant.inventoryQuantity || 0}
                                        </Text>
                                      </BlockStack>
                                    </InlineStack>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
                
                {selectedShopifyProducts.length > 0 && !editingEntry && (
                  <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f6f6f7', borderRadius: '4px' }}>
                    <Text variant="bodyMd" fontWeight="semibold">
                      Entries to Create:
                    </Text>
                    <div style={{ marginTop: '8px' }}>
                      {selectedShopifyProducts.map(productId => {
                        const product = shopifyProducts.find(p => p.id.toString() === productId);
                        const variantId = selectedVariants[productId];
                        const variant = variantId ? product.variants?.find(v => v.id.toString() === variantId) : null;
                        
                        if (variant) {
                          return (
                            <div key={`${productId}-${variantId}`} style={{ marginBottom: '4px' }}>
                              <Text variant="bodySm">
                                • {product.title} - {variant.title}
                              </Text>
                            </div>
                          );
                        } else if (product.variants && product.variants.length > 0) {
                          return (
                            <div key={productId} style={{ marginBottom: '4px' }}>
                              <Text variant="bodySm">
                                • {product.title} ({product.variants.length} variants)
                              </Text>
                              {product.variants.map(v => (
                                <div key={v.id} style={{ marginLeft: '16px', marginTop: '2px' }}>
                                  <Text variant="bodySm" tone="subdued">
                                    - {v.title}
                                  </Text>
                                </div>
                              ))}
                            </div>
                          );
                        } else {
                          return (
                            <div key={productId} style={{ marginBottom: '4px' }}>
                              <Text variant="bodySm">
                                • {product.title}
                              </Text>
                            </div>
                          );
                        }
                      })}
                    </div>
                  </div>
                )}
              </div>

              <TextField
                label="Display Name (optional)"
                value={entryFormData.displayName}
                onChange={(value) => handleEntryFormChange('displayName', value)}
                helpText="Custom name to display instead of product title"
              />

              <TextField
                label="Description (optional)"
                value={entryFormData.description}
                onChange={(value) => handleEntryFormChange('description', value)}
                helpText="Additional description for this entry"
                multiline={3}
              />

              <Select
                label="Pricing Type"
                options={[
                  { label: 'Included (Free)', value: 'included' },
                  { label: 'Fixed Price per Day', value: 'fixed_price' },
                  { label: 'Percentage of Base Price', value: 'percentage_of_base' }
                ]}
                value={entryFormData.pricingType}
                onChange={(value) => handleEntryFormChange('pricingType', value)}
              />

              {entryFormData.pricingType === 'fixed_price' && (
                <TextField
                  label="Price per Day ($)"
                  type="number"
                  step="0.01"
                  min="0"
                  value={entryFormData.price}
                  onChange={(value) => handleEntryFormChange('price', value)}
                  required
                  helpText="Fixed price per day for this add-on entry"
                />
              )}

              {entryFormData.pricingType === 'percentage_of_base' && (
                <TextField
                  label="Percentage (%)"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={entryFormData.price}
                  onChange={(value) => handleEntryFormChange('price', value)}
                  required
                  helpText="Percentage of the base rental price (0-100%)"
                />
              )}

              <FormLayout.Group>
                <TextField
                  label="Available Quantity"
                  type="number"
                  min="0"
                  value={entryFormData.availableQuantity}
                  onChange={(value) => handleEntryFormChange('availableQuantity', value)}
                  helpText="How many of this item are available for rental"
                  required
                />
                
                <TextField
                  label="Sort Order"
                  type="number"
                  value={entryFormData.sortOrder}
                  onChange={(value) => handleEntryFormChange('sortOrder', value)}
                  helpText="Lower numbers appear first"
                />
              </FormLayout.Group>
            </FormLayout>
          </Form>
        </Modal.Section>
      </Modal>
    </div>
  );
};

export default AddOns;
